import React, { useCallback, useEffect, useRef, useState } from "react"
import {
  Badge,
  Button,
  Card,
  CardBody,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
} from "reactstrap"
import Breadcrumbs from "components/Common/Breadcrumb"
import { useTranslation } from "react-i18next"
import { useDispatch } from "react-redux"
import { showAlert } from "store/reducers/alertReducer"
import {
  discardWebsiteBuilderDraft,
  fetchWebsiteBuilderConfig,
  publishWebsiteBuilderConfig,
  saveWebsiteBuilderDraft,
  saveWebsiteThemeBaseline,
} from "modules/website-builder/services/api"
import { WEBSITE_BUILDER_TYPES } from "modules/website-builder/constants/websiteTypes"
import {
  cloneQrWebsiteConfig,
  normalizeQrWebsiteBuilderForm,
} from "modules/website-builder/constants/qrFormUtils"
import {
  QrAssetsTab,
  QrBrandTab,
  QrPublishTab,
  QrTemplatesTab,
  QrTextTab,
} from "modules/website-builder/pages/QrWebsiteBuilderFields"
import { WebsiteBuilderTenantContext } from "modules/website-builder/WebsiteBuilderTenantContext"

const TABS = [
  { id: "templates", label: "Templates" },
  { id: "brand", label: "Brand & Theme" },
  { id: "assets", label: "Assets" },
  { id: "text", label: "Text Content" },
  { id: "publish", label: "Preview & Publish" },
]

const QrWebsiteBuilder = ({
  tenantId,
  tenantLabel = "",
  breadcrumbRoot = "Website Builder",
  readOnly = false,
}) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const websiteType = WEBSITE_BUILDER_TYPES.QR
  const [activeTab, setActiveTab] = useState("brand")
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [publishing, setPublishing] = useState(false)
  const [form, setForm] = useState(null)
  const [publishedSnapshot, setPublishedSnapshot] = useState(null)
  const [hasUnpublishedChanges, setHasUnpublishedChanges] = useState(false)
  const [publishedAt, setPublishedAt] = useState(null)
  const [layoutTemplates, setLayoutTemplates] = useState([])
  const [savedThemeBaseline, setSavedThemeBaseline] = useState(null)
  const [savedThemeBaselineAt, setSavedThemeBaselineAt] = useState(null)
  const [savingThemeBaseline, setSavingThemeBaseline] = useState(false)
  const [restoringThemeBaseline, setRestoringThemeBaseline] = useState(false)
  const formRef = useRef(null)

  document.title = tenantLabel
    ? `${t("QR Website Builder")} — ${tenantLabel}`
    : t("QR Website Builder")

  const applyFormChange = useCallback(updater => {
    const base = formRef.current
    if (!base) return

    const next = typeof updater === "function" ? updater(base) : updater
    if (!next) return

    const cloned = cloneQrWebsiteConfig(next)
    formRef.current = cloned
    setForm(cloned)
    setHasUnpublishedChanges(true)
  }, [])

  const loadConfig = useCallback(async () => {
    if (!tenantId) return

    setLoading(true)
    try {
      const response = await fetchWebsiteBuilderConfig(tenantId, websiteType)
      if (response?.status && response?.data) {
        const draft = normalizeQrWebsiteBuilderForm(response.data.draft || {}, tenantId)
        formRef.current = draft
        setForm(draft)
        setPublishedSnapshot(
          response.data.published
            ? normalizeQrWebsiteBuilderForm(response.data.published, tenantId)
            : null
        )
        setHasUnpublishedChanges(!!response.data.hasUnpublishedChanges)
        setPublishedAt(response.data.publishedAt || null)
        setLayoutTemplates(Array.isArray(response.data.templates) ? response.data.templates : [])
        setSavedThemeBaseline(
          response.data.savedThemeBaseline
            ? normalizeQrWebsiteBuilderForm(response.data.savedThemeBaseline, tenantId)
            : null
        )
        setSavedThemeBaselineAt(response.data.savedThemeBaselineAt || null)
      }
    } catch (_err) {
      dispatch(
        showAlert({
          text: t("Unable to load QR website builder settings"),
          type: "error",
        })
      )
    } finally {
      setLoading(false)
    }
  }, [dispatch, t, tenantId, websiteType])

  useEffect(() => {
    loadConfig()
  }, [loadConfig])

  const saveDraft = async ({ showSuccessAlert = true } = {}) => {
    if (readOnly) return false
    const currentForm = formRef.current
    if (!currentForm || !tenantId) return false
    setSaving(true)
    try {
      const response = await saveWebsiteBuilderDraft(tenantId, currentForm, websiteType)
      if (response?.status) {
        if (showSuccessAlert) {
          dispatch(showAlert({ text: t("Draft saved successfully"), type: "success" }))
        }
        if (response?.data?.draft) {
          const savedDraft = normalizeQrWebsiteBuilderForm(response.data.draft, tenantId)
          formRef.current = savedDraft
          setForm(savedDraft)
        }
        return true
      }

      dispatch(
        showAlert({
          text: response?.msg || t("Unable to save QR website draft"),
          type: "error",
        })
      )
      return false
    } catch (_err) {
      dispatch(showAlert({ text: t("Unable to save QR website draft"), type: "error" }))
      return false
    } finally {
      setSaving(false)
    }
  }

  const handleSaveDraft = () => saveDraft({ showSuccessAlert: true })

  const handlePublish = async () => {
    if (readOnly) return
    if (!formRef.current || !tenantId) return

    setPublishing(true)
    try {
      const saved = await saveDraft({ showSuccessAlert: false })
      if (!saved) return

      const response = await publishWebsiteBuilderConfig(tenantId, websiteType)
      if (response?.status) {
        dispatch(showAlert({ text: t("QR website published successfully"), type: "success" }))
        await loadConfig()
      } else {
        dispatch(
          showAlert({
            text: response?.msg || t("Unable to publish QR website"),
            type: "error",
          })
        )
      }
    } catch (_err) {
      dispatch(showAlert({ text: t("Unable to publish QR website"), type: "error" }))
    } finally {
      setPublishing(false)
    }
  }

  const handleDiscard = async () => {
    if (readOnly || !tenantId) return

    try {
      const response = await discardWebsiteBuilderDraft(tenantId, websiteType)
      if (response?.status) {
        dispatch(showAlert({ text: t("Draft changes discarded"), type: "success" }))
        await loadConfig()
      }
    } catch (_err) {
      dispatch(showAlert({ text: t("Unable to discard draft"), type: "error" }))
    }
  }

  const handleSaveThemeBaseline = async () => {
    if (readOnly) return
    const currentForm = formRef.current
    if (!currentForm || !tenantId) return

    const snapshot = cloneQrWebsiteConfig(currentForm)
    setSavingThemeBaseline(true)
    try {
      const response = await saveWebsiteThemeBaseline(tenantId, snapshot, websiteType)
      if (response?.status) {
        const saved =
          response.data?.savedThemeBaseline
            ? normalizeQrWebsiteBuilderForm(response.data.savedThemeBaseline, tenantId)
            : normalizeQrWebsiteBuilderForm(snapshot, tenantId)
        setSavedThemeBaseline(saved)
        setSavedThemeBaselineAt(
          response.data?.savedThemeBaselineAt || new Date().toISOString()
        )
        dispatch(
          showAlert({
            text: t(
              "Your QR website design has been saved. Use Reset to restore it anytime."
            ),
            type: "success",
          })
        )
      } else {
        dispatch(
          showAlert({
            text: response?.msg || t("Unable to save QR website snapshot"),
            type: "error",
          })
        )
      }
    } catch (_err) {
      dispatch(showAlert({ text: t("Unable to save QR website snapshot"), type: "error" }))
    } finally {
      setSavingThemeBaseline(false)
    }
  }

  const handleRestoreThemeBaseline = async () => {
    if (readOnly || !tenantId) return

    setRestoringThemeBaseline(true)
    try {
      const response = await fetchWebsiteBuilderConfig(tenantId, websiteType)
      const snapshot = response?.data?.savedThemeBaseline

      if (!response?.status || !snapshot?.colors) {
        dispatch(
          showAlert({
            text: t(
              "No saved design found. Customize your QR website and click Save My Design first."
            ),
            type: "error",
          })
        )
        return
      }

      const normalized = normalizeQrWebsiteBuilderForm(snapshot, tenantId)
      setSavedThemeBaseline(normalized)
      setSavedThemeBaselineAt(response.data?.savedThemeBaselineAt || null)
      applyFormChange(() => normalized)
      dispatch(
        showAlert({
          text: t("Design restored from your saved version. Save draft to keep changes."),
          type: "success",
        })
      )
    } catch (_err) {
      dispatch(showAlert({ text: t("Unable to restore saved design"), type: "error" }))
    } finally {
      setRestoringThemeBaseline(false)
    }
  }

  if (!tenantId) {
    return null
  }

  if (loading || !form) {
    return (
      <div className="page-content">
        <Breadcrumbs title={t(breadcrumbRoot)} breadcrumbItem={t("QR Website Builder")} />
        <Card>
          <CardBody>{t("Loading...")}</CardBody>
        </Card>
      </div>
    )
  }

  return (
    <WebsiteBuilderTenantContext.Provider value={{ tenantId, websiteType }}>
      <div className="page-content">
        <Breadcrumbs title={t(breadcrumbRoot)} breadcrumbItem={t("QR Website Builder")} />

        <Card>
          <CardBody>
            <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
              <div>
                <h4 className="mb-1">{t("QR Website Builder")}</h4>
                {tenantLabel ? (
                  <p className="text-muted mb-1">
                    {t("Tenant")}: <strong>{tenantLabel}</strong>
                  </p>
                ) : null}
                <p className="text-muted mb-0">
                  {t("Customize the QR ordering website without affecting ordering APIs.")}
                </p>
              </div>
              <div className="d-flex flex-wrap gap-2 align-items-center">
                {hasUnpublishedChanges ? (
                  <Badge color="warning">{t("Unpublished changes")}</Badge>
                ) : (
                  <Badge color="success">{t("Published")}</Badge>
                )}
                <Button
                  color="light"
                  onClick={handleDiscard}
                  disabled={readOnly || !hasUnpublishedChanges}
                >
                  {t("Discard Draft")}
                </Button>
                <Button color="primary" outline onClick={handleSaveDraft} disabled={readOnly || saving}>
                  {saving ? t("Saving...") : t("Save Draft")}
                </Button>
                <Button color="primary" onClick={handlePublish} disabled={readOnly || publishing}>
                  {publishing ? t("Publishing...") : t("Publish")}
                </Button>
              </div>
            </div>

            <Nav tabs className="nav-tabs-custom mb-3">
              {TABS.map(tab => (
                <NavItem key={tab.id}>
                  <NavLink
                    className={activeTab === tab.id ? "active" : ""}
                    onClick={() => setActiveTab(tab.id)}
                    style={{ cursor: "pointer" }}
                  >
                    {t(tab.label)}
                  </NavLink>
                </NavItem>
              ))}
            </Nav>

            <TabContent activeTab={activeTab}>
              <TabPane tabId="templates">
                <QrTemplatesTab templates={layoutTemplates} form={form} t={t} />
              </TabPane>
              <TabPane tabId="brand">
                <QrBrandTab
                  form={form}
                  t={t}
                  onChange={applyFormChange}
                  savedThemeBaseline={savedThemeBaseline}
                  savedThemeBaselineAt={savedThemeBaselineAt}
                  savingThemeBaseline={savingThemeBaseline}
                  restoringThemeBaseline={restoringThemeBaseline}
                  onSaveThemeBaseline={handleSaveThemeBaseline}
                  onRestoreThemeBaseline={handleRestoreThemeBaseline}
                  readOnly={readOnly}
                />
              </TabPane>
              <TabPane tabId="assets">
                <QrAssetsTab form={form} t={t} onChange={applyFormChange} />
              </TabPane>
              <TabPane tabId="text">
                <QrTextTab form={form} t={t} onChange={applyFormChange} />
              </TabPane>
              <TabPane tabId="publish">
                <QrPublishTab
                  form={form}
                  t={t}
                  publishedAt={publishedAt}
                  publishedSnapshot={publishedSnapshot}
                  hasUnpublishedChanges={hasUnpublishedChanges}
                />
              </TabPane>
            </TabContent>
          </CardBody>
        </Card>
      </div>
    </WebsiteBuilderTenantContext.Provider>
  )
}

export default QrWebsiteBuilder
